import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../api/client";

function Bar({ label, value, max, suffix }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="suivi-bar-row">
      <span className="suivi-bar-label" title={label}>{label}</span>
      <span className="suivi-bar-track">
        <span className="suivi-bar-fill" style={{ width: `${pct}%` }} />
      </span>
      <span className="suivi-bar-value">{value}{suffix || ""}</span>
    </div>
  );
}

function StatCard({ titre, valeur, sous }) {
  return (
    <div className="suivi-card">
      <div className="suivi-card-value">{valeur}</div>
      <div className="suivi-card-title">{titre}</div>
      {sous && <div className="suivi-card-sub muted">{sous}</div>}
    </div>
  );
}

const PERIODES = [
  [7, "7 jours"],
  [30, "30 jours"],
  [90, "90 jours"],
  [365, "1 an"],
];

export default function SuiviAdmin() {
  const [data, setData] = useState(null);
  const [jours, setJours] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      setData(await api.get(`/admin/analytics/resume?jours=${jours}`));
    } catch (e) {
      setError(e.message || "Impossible de charger les statistiques.");
    } finally { setLoading(false); }
  }, [jours]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="suivi">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.6rem" }}>
        <p className="muted" style={{ margin: 0 }}>
          Mesure d'audience anonyme — sans cookie, sans donnée personnelle conservée.
        </p>
        <div className="actu-filter-tabs">
          {PERIODES.map(([j, lab]) => (
            <button key={j} className={jours === j ? "active" : ""}
                    onClick={() => setJours(j)}>{lab}</button>
          ))}
        </div>
      </div>

      {loading && <p className="muted mt-2">Chargement…</p>}
      {error && <div className="error mt-2">{error}</div>}

      {data && !loading && (
        <>
          <div className="suivi-cards mt-3">
            <StatCard titre="Pages vues" valeur={data.totaux.pages}
                      sous={`sur ${data.periode_jours} jours`} />
            <StatCard titre="Visiteurs estimés" valeur={data.totaux.visiteurs}
                      sous="estimation anonyme" />
            <StatCard titre="Connexions" valeur={data.totaux.logins}
                      sous="comptes authentifiés" />
            <StatCard titre="Comptes" valeur={data.comptes.total}
                      sous={`+${data.comptes.recents} récents · ${data.comptes.en_attente} en attente`} />
          </div>

          {/* Trafic par jour */}
          <h3 className="mt-3">Trafic par jour</h3>
          {data.serie.length === 0 ? (
            <p className="muted">Pas encore de données sur cette période.</p>
          ) : (
            <div className="suivi-block">
              {(() => {
                const max = Math.max(...data.serie.map((s) => s.pages_vues), 1);
                return data.serie.map((s) => (
                  <Bar key={s.jour} label={s.jour} value={s.pages_vues} max={max} />
                ));
              })()}
            </div>
          )}

          <div className="suivi-two-col mt-3">
            {/* Appareils */}
            <div>
              <h3>Appareils</h3>
              {data.appareils.length === 0 ? <p className="muted">—</p> : (
                <div className="suivi-block">
                  {(() => {
                    const max = Math.max(...data.appareils.map((a) => a.vues), 1);
                    return data.appareils.map((a) => (
                      <Bar key={a.appareil} label={a.appareil} value={a.vues} max={max} />
                    ));
                  })()}
                </div>
              )}
            </div>

            {/* Systèmes */}
            <div>
              <h3>Systèmes</h3>
              {data.systemes.length === 0 ? <p className="muted">—</p> : (
                <div className="suivi-block">
                  {(() => {
                    const max = Math.max(...data.systemes.map((s) => s.vues), 1);
                    return data.systemes.map((s) => (
                      <Bar key={s.os} label={s.os} value={s.vues} max={max} />
                    ));
                  })()}
                </div>
              )}
            </div>
          </div>

          {/* Pages les plus vues */}
          <h3 className="mt-3">Pages les plus consultées</h3>
          {data.pages.length === 0 ? <p className="muted">—</p> : (
            <div className="suivi-block">
              {(() => {
                const max = Math.max(...data.pages.map((p) => p.vues), 1);
                return data.pages.map((p) => (
                  <Bar key={p.chemin} label={p.chemin} value={p.vues} max={max} />
                ));
              })()}
            </div>
          )}

          <p className="muted mt-3" style={{ fontSize: "0.82em" }}>
            Les « visiteurs estimés » reposent sur une empreinte technique
            éphémère (non conservée, renouvelée chaque jour) : c'est une
            estimation, pas un comptage exact d'individus. Les robots sont exclus.
          </p>
        </>
      )}
    </div>
  );
}
